import { Quiz, QuizResultLink } from "@/types/quiz"

// Quiz state interface for initial state
export interface QuizState {
  currentStep: string
  stepHistory: string[]
  answers: Record<string, string>
  progressHistory: number[]
  isTransitioning: boolean
  transitionDirection: "up" | "down" | "none"
}

const NEWSLETTER_HEADER = "Stay tuned!"
const NEWSLETTER_TYPE = "reference"
const NEWSLETTER_LABEL = "Subscribe"
const NEWSLETTER_URI = "subscribe"
const NEWSLETTER_LINK: QuizResultLink = {
  type: NEWSLETTER_TYPE,
  label: NEWSLETTER_LABEL,
  uri: NEWSLETTER_URI,
}

const MISSION_HEADER = "Try this out!"
const MISSION_TYPE = "link"
const MISSION_LABEL = "Open activity"

const INTERSTITIAL_IMAGE_URL =
  "https://app.pandorasway.com/images/quiz-interstitial.webp"

const QUIZ: Quiz = {
  questions: [
    {
      code: "question_1",
      value: "Which of these feels bigger right now?",
      choices: [
        {
          code: "question_1_screens",
          value: "Screens have hijacked my child(ren)’s attention",
          progress: 25,
        },
        {
          code: "question_1_safety",
          value: "My kid doesn’t understand basic internet safety",
          progress: 33,
        },
        {
          code: "question_1_social_life",
          value:
            "My kid(s)’ social circle is moving online, but they aren’t ready",
          progress: 33,
        },
        {
          code: "question_1_none",
          value: "None of these, actually!",
          progress: 50,
        },
      ],
    },
    {
      code: "question_2_family",
      value: "Which of these describes your family best?",
      choices: [
        {
          code: "question_2_family_in",
          value: "At least one of my kids is between 8-13",
          progress: 50,
        },
        {
          code: "question_2_family_none",
          value: "None of my kid(s) are 8-13",
          progress: 100,
        },
      ],
    },
    {
      code: "question_3_pain",
      value: "Which of these describes your family best?",
      choices: [
        {
          code: "question_3_pain_quality_time",
          value: "We don’t spend enough quality time together",
          progress: 100,
        },
        {
          code: "question_3_pain_tech_habits",
          value: "Our bad tech habits are affecting our kids",
          progress: 100,
        },
        {
          code: "question_3_pain_losing_joy",
          value: "I’m losing the joy of parenting",
          progress: 75,
        },
        {
          code: "question_3_pain_none",
          value: "Not quite any of these",
          progress: 100,
        },
      ],
    },
    {
      code: "question_4_decision",
      value: "Who makes the decisions around tech at home?",
      choices: [
        {
          code: "question_4_decision_solo",
          value: "It’s me, or mostly me",
          progress: 100,
        },
        {
          code: "question_4_decision_harmony",
          value: "I have one or more co-parents, we mostly agree",
          progress: 100,
        },
        {
          code: "question_4_decision_conflict",
          value: "I have one or more co-parents, we struggle to agree",
          progress: 100,
        },
        {
          code: "question_4_decision_high_conflict",
          value: "I have one or more co-parents, it’s an endless fight",
          progress: 100,
        },
      ],
    },
    {
      code: "question_2_safe_family",
      value: "Which of these describes your family best?",
      choices: [
        {
          code: "question_2_safe_family_in",
          value: "At least one of my kids is between 8-13",
          progress: 66,
        },
        {
          code: "question_2_safe_family_none",
          value: "None of my kid(s) are 8-13",
          progress: 100,
        },
      ],
    },
    {
      code: "question_2_social_family",
      value: "Which of these describes your family best?",
      choices: [
        {
          code: "question_2_social_family_in",
          value: "At least one of my kids is between 8-13",
          progress: 66,
        },
        {
          code: "question_2_social_family_none",
          value: "None of my kid(s) are 8-13",
          progress: 100,
        },
      ],
    },
    {
      code: "question_2_none_family",
      value: "Which of these describes your family best?",
      choices: [
        {
          code: "question_2_none_family_in",
          value: "At least one of my kids is between 8-13",
          progress: 100,
        },
        {
          code: "question_2_none_family_none",
          value: "None of my kid(s) are 8-13",
          progress: 100,
        },
      ],
    },
    {
      code: "question_3_concern",
      value: "What’s your biggest concern?",
      choices: [
        {
          code: "question_3_concern_strangers",
          value: "My kid meeting strangers online",
          progress: 100,
        },
        {
          code: "question_3_concern_drama",
          value: "My kid getting into social drama in group chats",
          progress: 100,
        },
        {
          code: "question_3_concern_inappropriate",
          value: "My kid being exposed to inappropriate content",
          progress: 100,
        },
        {
          code: "question_3_concern_else",
          value: "There’s something else that worries me most",
          progress: 100,
        },
      ],
    },
    {
      code: "question_3_resonate",
      value: "Which of these resonate more?",
      choices: [
        {
          code: "question_3_resonate_further",
          value: "More online time will pull them further from real life",
          progress: 100,
        },
        {
          code: "question_3_resonate_bullied",
          value: "My child may get bullied for not being included",
          progress: 100,
        },
        {
          code: "question_3_resonate_drama",
          value: "They aren’t ready for all the drama in group chats",
          progress: 100,
        },
        {
          code: "question_3_resonate_hesitant",
          value: "They’re hesitant to ask for help when they need it",
          progress: 100,
        },
      ],
    },
  ],
  interstitials: [
    {
      code: "interstitial_1_screens",
      value: "You're not alone. Screens even hijack us sometimes!",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_1_safety",
      value:
        "Ugh, yes, the internet can be a scary place. Building resilience early is key!",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_1_social_life",
      value:
        "We see that trend all over, too. It's a hard balance to strike, but we may be able to help!",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_1_none",
      value:
        "We feel you — every family is different. We may soon have something for yours, though!",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_2_family_in",
      value:
        "Great! Our first product is focused on these ages. Think about the next questions with your tween(s) in mind",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_3_pain_losing_joy",
      value:
        "We hear you - screen battles are exhausting! We can help you and your family rediscover joy.",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_2_safe_family_in",
      value:
        "Great! Our first product is focused on these ages. Think about the next questions with your tween(s) in mind",
      image: INTERSTITIAL_IMAGE_URL,
    },
    {
      code: "interstitial_2_social_family_in",
      value:
        "Great! Our first product is focused on these ages. Think about the next questions with your tween(s) in mind",
      image: INTERSTITIAL_IMAGE_URL,
    },
  ],
  results: [
    {
      code: "result_2_family_none",
      header: NEWSLETTER_HEADER,
      description:
        "Our current offerings are for kids 8-13, but it's never too early! Sign up or follow us to be the first to know about future releases.",
      link: NEWSLETTER_LINK,
    },
    {
      code: "result_3_pain_quality_time",
      header: MISSION_HEADER,
      description:
        "Sometimes all you need is a little structure. Try this activity to jump-start joy in your home.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/ff5cfaa2-b707-4db4-adcd-21ff686e94ec/",
      },
    },
    {
      code: "result_3_pain_tech_habits",
      header: MISSION_HEADER,
      description:
        "A little self-reflection can have a huge impact in changing negative screen patterns. Here’s a tool to help.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/4aaea6fa-39ad-411d-aaf3-40a4ddcef032/",
      },
    },
    {
      code: "result_3_none",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "We’re always adding new  resources to support you in your kids’ tech journey. Sign up for our newsletter and follow us to stay in touch.",
    },
    {
      code: "result_4_decision_solo",
      header: MISSION_HEADER,
      description:
        "Sometimes all you need is a little structure. Try this activity to jump-start some joy in your home.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/f033519b-a5b3-4400-8899-e0763c376442/",
      },
    },
    {
      code: "result_4_decision_harmony",
      header: MISSION_HEADER,
      description:
        "Sometimes all you need is a little structure. Try this activity to jump-start some joy in your home.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/52fd5853-85c0-43d2-a98b-3d1fc396d610/",
      },
    },
    {
      code: "result_4_decision_conflict",
      header: MISSION_HEADER,
      description:
        "If you spend a lot of time in conflict, try this activity to jump-start some joy in your home.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/ada25688-efac-4339-892b-22e592a97df9/",
      },
    },
    {
      code: "result_4_decision_high_conflict",
      header: MISSION_HEADER,
      description:
        "If you spend a lot of time in conflict, try this activity to jump-start some joy in your home.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/85abc96d-48c6-4654-a2fc-c73e545c2149/",
      },
    },
    {
      code: "result_2_safe_family_none",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Our current offerings are for kids 8-13, but it's never too early! Sign up or follow us to be the first to know about future releases.",
    },
    {
      code: "result_3_concern_strangers",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Internet safety is a huge issue!  Follow us to be notified when we launch resources to help your kid(s) make healthy choices online.",
    },
    {
      code: "result_3_concern_drama",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Group chats can get complicated! This activity gives your child the experience of creating healthy boundaries.",
    },
    {
      code: "result_3_concern_inappropriate",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Inappropriate content is a huge issue! Sign up for our newsletter and follow us to be notified when we launch tools or resources that can help.",
    },
    {
      code: "result_3_concern_else",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Internet safety is a huge issue. Sign up for our newsletter and follow us to be notified when we launch tools or resources that can help.",
    },
    {
      code: "result_2_social_family_none",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Our current offerings are for kids 8-13, but it's never too early! Sign up or follow us to be the first to know about future releases.",
    },
    {
      code: "result_2_none_family_in",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "If our current offerings don't fit your needs, sign up to be the first to hear of what's ahead! Or help us build it by reaching out on socials.",
    },
    {
      code: "result_2_none_family_none",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Our current offerings are for kids 8-13, but it's never too early! Sign up or follow us to be the first to know about future releases.",
    },
    {
      code: "result_3_resonate_further",
      header: MISSION_HEADER,
      description:
        "This mission helps you build stronger real world family connections while having fun in the process!",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/ff5cfaa2-b707-4db4-adcd-21ff686e94ec/",
      },
    },
    {
      code: "result_3_resonate_bullied",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Sorry you are dealing with this. :( Follow us to be notified whenever we launch anti-bullying and other parenting resources.",
    },
    {
      code: "result_3_resonate_drama",
      header: MISSION_HEADER,
      description:
        "Group chats can get complicated. This activity supports you in helping your child practice good group chat habits.",
      link: {
        type: MISSION_TYPE,
        label: MISSION_LABEL,
        uri: "https://app.pandorasway.com/missions/c27b4c9c-00aa-4db6-a496-b6c42534b308/",
      },
    },
    {
      code: "result_3_resonate_hesitant",
      header: NEWSLETTER_HEADER,
      link: NEWSLETTER_LINK,
      description:
        "Follow us for resources to help you build trust with your kid(s) — and other common digital parenting challenges.",
    },
  ],
  tree: {
    question_1_screens: ["interstitial_1_screens", "question_2_family"],
    question_2_family_in: ["interstitial_2_family_in", "question_3_pain"],
    question_2_family_none: "result_2_family_none",
    question_3_pain_quality_time: "result_3_pain_quality_time",
    question_3_pain_tech_habits: "result_3_pain_tech_habits",
    question_3_pain_losing_joy: [
      "interstitial_3_pain_losing_joy",
      "question_4_decision",
    ],
    question_3_pain_none: "result_3_none",
    question_4_decision_solo: "result_4_decision_solo",
    question_4_decision_harmony: "result_4_decision_harmony",
    question_4_decision_conflict: "result_4_decision_conflict",
    question_4_decision_high_conflict: "result_4_decision_high_conflict",
    question_1_safety: ["interstitial_1_safety", "question_2_safe_family"],
    question_2_safe_family_in: [
      "interstitial_2_safe_family_in",
      "question_3_concern",
    ],
    question_2_safe_family_none: "result_2_safe_family_none",
    question_3_concern_strangers: "result_3_concern_strangers",
    question_3_concern_drama: "result_3_concern_drama",
    question_3_concern_inappropriate: "result_3_concern_inappropriate",
    question_3_concern_else: "result_3_concern_else",
    question_1_social_life: [
      "interstitial_1_social_life",
      "question_2_social_family",
    ],
    question_2_social_family_in: [
      "interstitial_2_social_family_in",
      "question_3_resonate",
    ],
    question_3_resonate_further: "result_3_resonate_further",
    question_3_resonate_bullied: "result_3_resonate_bullied",
    question_3_resonate_drama: "result_3_resonate_drama",
    question_3_resonate_hesitant: "result_3_resonate_hesitant",
    question_2_social_family_none: "result_2_social_family_none",
    question_1_none: ["interstitial_1_none", "question_2_none_family"],
    question_2_none_family_in: "result_2_none_family_in",
    question_2_none_family_none: "result_2_none_family_none",
  },
}

export const getInitialState = (): QuizState => ({
  currentStep: "question_1",
  stepHistory: ["question_1"],
  answers: {},
  progressHistory: [0],
  isTransitioning: false,
  transitionDirection: "none",
})

export default QUIZ
